/**
 * SubscriptionRegistry - Pattern-based subscription management
 *
 * Single Responsibility: Maintain registry of pattern-based subscriptions with automatic cleanup
 *
 * Responsible for:
 * - Registering/unregistering pattern-based subscriptions
 * - Looking up active subscriptions by pattern
 * - Auto-cleanup when subscriptions are unsubscribed
 * - Reporting active subscription statistics
 */

import type { ISubscription } from '../types';
import { TopicMatcher, type ITopicMatcher } from './TopicMatcher';
import { generatePrefixedId } from '../utils';
import { createModuleLogger } from '../utils';

const logger = createModuleLogger('SubscriptionRegistry');

export interface ISubscriptionRegistry {
  /**
   * Register a pattern-based subscription
   *
   * @param pattern - Topic pattern (supports wildcards like "form.*")
   * @param subscription - Subscription to track
   * @returns registration ID
   */
  register(pattern: string, subscription: ISubscription): string;

  /**
   * Unregister a subscription by registration ID
   * @param registrationId - ID returned from register()
   */
  unregister(registrationId: string): void;

  /**
   * Get all subscriptions matching a pattern
   * @param pattern - Topic pattern to search for
   * @returns Array of matching subscriptions
   */
  getSubscriptionsByPattern(pattern: string): ISubscription[];

  /**
   * Get all active subscriptions
   * @returns Array of all active subscriptions
   */
  getAll(): ISubscription[];

  /**
   * Get subscription count by pattern
   * @param pattern - Topic pattern
   * @returns Number of subscriptions for this pattern
   */
  getCountByPattern(pattern: string): number;

  /**
   * Get total active subscription count
   * @returns Number of active subscriptions
   */
  getCount(): number;

  /**
   * Check if a pattern has any active subscriptions
   * @param pattern - Topic pattern
   * @returns true if pattern has subscriptions
   */
  hasSubscriptions(pattern: string): boolean;

  /**
   * Clear all subscriptions
   */
  clear(): void;

  /**
   * Get registry statistics
   */
  getStatistics(): {
    totalSubscriptions: number;
    patternCount: number;
    patterns: Array<{
      pattern: string;
      count: number;
      activeCount: number;
    }>;
  };
}

interface RegistryEntry {
  registrationId: string;
  pattern: string;
  subscription: ISubscription;
  registeredAt: Date;
  isActive: boolean;
}

export class SubscriptionRegistry implements ISubscriptionRegistry {
  private entries: Map<string, RegistryEntry> = new Map();
  private patternIndex: Map<string, Set<string>> = new Map(); // pattern -> Set<registrationId>
  private matcher: ITopicMatcher;

  constructor(matcher?: ITopicMatcher) {
    this.matcher = matcher || new TopicMatcher();
  }

  register(pattern: string, subscription: ISubscription): string {
    // Validate pattern
    try {
      this.matcher.validate(pattern);
    } catch (error) {
      logger.warn(`[SubscriptionRegistry] Invalid pattern "${pattern}": ${error}`);
      throw error;
    }

    const registrationId = generatePrefixedId('reg');

    const entry: RegistryEntry = {
      registrationId,
      pattern,
      subscription,
      registeredAt: new Date(),
      isActive: true,
    };

    this.entries.set(registrationId, entry);

    // Update pattern index
    if (!this.patternIndex.has(pattern)) {
      this.patternIndex.set(pattern, new Set());
    }
    this.patternIndex.get(pattern)!.add(registrationId);

    logger.debug(
      `[SubscriptionRegistry] Registered pattern="${pattern}" id=${registrationId} (total=${this.entries.size})`,
    );

    return registrationId;
  }

  unregister(registrationId: string): void {
    const entry = this.entries.get(registrationId);
    if (!entry) {
      logger.warn(`[SubscriptionRegistry] Unregister: ID not found ${registrationId}`);
      return;
    }

    entry.isActive = false;
    this.entries.delete(registrationId);

    // Clean up pattern index
    const patterns = this.patternIndex.get(entry.pattern);
    if (patterns) {
      patterns.delete(registrationId);
      if (patterns.size === 0) {
        this.patternIndex.delete(entry.pattern);
      }
    }

    logger.debug(
      `[SubscriptionRegistry] Unregistered pattern="${entry.pattern}" id=${registrationId} (total=${this.entries.size})`,
    );
  }

  getSubscriptionsByPattern(pattern: string): ISubscription[] {
    const registrationIds = this.patternIndex.get(pattern);
    if (!registrationIds || registrationIds.size === 0) {
      return [];
    }

    const subscriptions: ISubscription[] = [];
    for (const regId of registrationIds) {
      const entry = this.entries.get(regId);
      if (entry && entry.isActive && entry.subscription.active) {
        subscriptions.push(entry.subscription);
      }
    }

    return subscriptions;
  }

  getAll(): ISubscription[] {
    const subscriptions: ISubscription[] = [];

    for (const entry of this.entries.values()) {
      if (entry.isActive && entry.subscription.active) {
        subscriptions.push(entry.subscription);
      }
    }

    return subscriptions;
  }

  getCountByPattern(pattern: string): number {
    const registrationIds = this.patternIndex.get(pattern);
    if (!registrationIds) {
      return 0;
    }

    let count = 0;
    for (const regId of registrationIds) {
      const entry = this.entries.get(regId);
      if (entry && entry.isActive && entry.subscription.active) {
        count++;
      }
    }

    return count;
  }

  getCount(): number {
    let count = 0;
    for (const entry of this.entries.values()) {
      if (entry.isActive && entry.subscription.active) {
        count++;
      }
    }
    return count;
  }

  hasSubscriptions(pattern: string): boolean {
    return this.getCountByPattern(pattern) > 0;
  }

  clear(): void {
    const count = this.entries.size;
    this.entries.clear();
    this.patternIndex.clear();
    logger.debug(`[SubscriptionRegistry] Cleared ${count} registrations`);
  }

  getStatistics(): {
    totalSubscriptions: number;
    patternCount: number;
    patterns: Array<{
      pattern: string;
      count: number;
      activeCount: number;
    }>;
  } {
    const patterns: Array<{
      pattern: string;
      count: number;
      activeCount: number;
    }> = [];

    for (const [pattern, registrationIds] of this.patternIndex) {
      let activeCount = 0;
      for (const regId of registrationIds) {
        const entry = this.entries.get(regId);
        if (entry && entry.isActive && entry.subscription.active) {
          activeCount++;
        }
      }

      patterns.push({
        pattern,
        count: registrationIds.size,
        activeCount,
      });
    }

    return {
      totalSubscriptions: this.getCount(),
      patternCount: this.patternIndex.size,
      patterns: patterns.sort((a, b) => a.pattern.localeCompare(b.pattern)),
    };
  }
}
